import React, { useEffect, useMemo, useState } from 'react';
import { Button, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaFileExcel } from 'react-icons/fa';
import Loading from '~/Components/Loading';
import { salvarEmExcel } from '~/Utils/Helpers';
import { buscarDetalhamentoVendasFcDw } from '~/Services/Service';
import CelulaComTooltip from './CelulaComTooltip';
import TabelaFluxoCaixa from './TabelaFluxoCaixa';
import {
  PERCENTUAIS_REFERENCIA,
  formatarMoeda,
  getMensagemErro,
  normalizarLista,
} from '../utils';

const TAMANHO_PAGINA = 50;
const PERCENTUAL_REFERENCIA_DEFAULT = 0.9;

const getItemId = item =>
  `venda-${item.empreendimento || 'sem-empreendimento'}-${item.ano ||
    'sem-ano'}-${item.ano_mes || 'sem-mes'}-${item.tipo_parcela ||
    'sem-parcela'}-${item.tipo_titulo || 'sem-titulo'}-${item.valor_a_receber ||
    0}-${item.comissao || 0}`;

const normalizarResponse = response => {
  const itens = normalizarLista(
    response && response.itens ? response.itens : []
  );
  const linhas = itens.map(item => ({
    ...item,
    id: getItemId(item),
  }));

  if (response && response.total) {
    linhas.push({
      ...response.total,
      empreendimento: 'Total',
      id: 'total-detalhamento-vendas',
      is_total: true,
    });
  }

  return linhas;
};

const AbaDetalhamentoVendas = ({ filtros }) => {
  const [loading, setLoading] = useState(false);
  const [linhas, setLinhas] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [percentualReferencia, setPercentualReferencia] = useState(
    PERCENTUAL_REFERENCIA_DEFAULT
  );

  useEffect(() => {
    setPagina(1);
  }, [filtros, percentualReferencia]);

  useEffect(() => {
    let ativo = true;

    const carregarDados = async () => {
      setLoading(true);
      try {
        const response = await buscarDetalhamentoVendasFcDw({
          ...filtros,
          percentual_referencia: percentualReferencia,
          pagina,
          tamanho_pagina: TAMANHO_PAGINA,
        });

        if (!ativo) return;

        const total =
          response && response.total_registros ? response.total_registros : 0;
        const totalPaginas = Math.max(1, Math.ceil(total / TAMANHO_PAGINA));

        if (pagina > totalPaginas) {
          setPagina(totalPaginas);
          return;
        }

        setLinhas(normalizarResponse(response));
        setTotalRegistros(total);
      } catch (err) {
        if (ativo) {
          toast.error(
            getMensagemErro(err, 'Erro ao carregar Detalhamento de Vendas.')
          );
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregarDados();

    return () => {
      ativo = false;
    };
  }, [filtros, pagina, percentualReferencia]);

  const colunas = useMemo(
    () => [
      {
        title: 'Empreendimento',
        key: 'empreendimento',
        ordenacao: true,
        width: '260px',
        render: (value, item) =>
          item.is_total ? (
            <strong>Total</strong>
          ) : (
            <CelulaComTooltip id={item.id} texto={value || '-'}>
              {value || '-'}
            </CelulaComTooltip>
          ),
      },
      {
        title: 'Ano',
        key: 'ano',
        align: 'center',
        ordenacao: true,
        width: '82px',
      },
      {
        title: 'Ano/Mês',
        key: 'ano_mes',
        align: 'center',
        ordenacao: true,
        width: '100px',
      },
      {
        title: 'Tipo da Parcela',
        key: 'tipo_parcela',
        ordenacao: true,
        width: '170px',
        render: (value, item) => (
          <CelulaComTooltip id={`${item.id}-parcela`} texto={value || '-'}>
            {value || '-'}
          </CelulaComTooltip>
        ),
      },
      {
        title: 'Tipo do Título',
        key: 'tipo_titulo',
        ordenacao: true,
        width: '160px',
        render: (value, item) => (
          <CelulaComTooltip id={`${item.id}-titulo`} texto={value || '-'}>
            {value || '-'}
          </CelulaComTooltip>
        ),
      },
      {
        title: 'Valor a Receber',
        key: 'valor_a_receber',
        align: 'right',
        ordenacao: true,
        width: '150px',
        render: (value, item) =>
          item.is_total ? (
            <strong>{formatarMoeda(value)}</strong>
          ) : (
            formatarMoeda(value)
          ),
        excel: item => formatarMoeda(item.valor_a_receber),
      },
      {
        title: 'Comissão',
        key: 'comissao',
        align: 'right',
        ordenacao: true,
        width: '130px',
        render: (value, item) =>
          item.is_total ? (
            <strong>{formatarMoeda(value)}</strong>
          ) : (
            formatarMoeda(value)
          ),
        excel: item => formatarMoeda(item.comissao),
      },
    ],
    []
  );

  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / TAMANHO_PAGINA));

  const renderPaginacao = () => {
    if (totalRegistros <= TAMANHO_PAGINA) return null;

    const itens = [];
    const maxBotoes = 5;
    let inicioPaginas = Math.max(1, pagina - Math.floor(maxBotoes / 2));
    let fimPaginas = Math.min(totalPaginas, inicioPaginas + maxBotoes - 1);

    if (fimPaginas - inicioPaginas < maxBotoes - 1) {
      inicioPaginas = Math.max(1, fimPaginas - maxBotoes + 1);
    }

    itens.push(
      <Pagination.First
        disabled={pagina === 1}
        key="first"
        onClick={() => setPagina(1)}
      />
    );
    itens.push(
      <Pagination.Prev
        disabled={pagina === 1}
        key="prev"
        onClick={() => setPagina(pagina - 1)}
      />
    );

    if (inicioPaginas > 1) {
      itens.push(<Pagination.Ellipsis disabled key="ellipsis-start" />);
    }

    for (let numero = inicioPaginas; numero <= fimPaginas; numero += 1) {
      const paginaAtual = numero;
      itens.push(
        <Pagination.Item
          active={paginaAtual === pagina}
          key={`pagina-${paginaAtual}`}
          onClick={() => setPagina(paginaAtual)}
        >
          {paginaAtual}
        </Pagination.Item>
      );
    }

    if (fimPaginas < totalPaginas) {
      itens.push(<Pagination.Ellipsis disabled key="ellipsis-end" />);
    }

    itens.push(
      <Pagination.Next
        disabled={pagina >= totalPaginas}
        key="next"
        onClick={() => setPagina(pagina + 1)}
      />
    );
    itens.push(
      <Pagination.Last
        disabled={pagina >= totalPaginas}
        key="last"
        onClick={() => setPagina(totalPaginas)}
      />
    );

    return (
      <div className="fluxo-caixa-dw-pagination">
        <small className="text-muted">
          Mostrando {totalRegistros > 0 ? (pagina - 1) * TAMANHO_PAGINA + 1 : 0}
          {' até '}
          {Math.min(pagina * TAMANHO_PAGINA, totalRegistros)}
          {' de '}
          {totalRegistros}
          {' registros'}
        </small>
        <Pagination className="mb-0">{itens}</Pagination>
      </div>
    );
  };

  const exportar = async () => {
    try {
      await salvarEmExcel(
        {
          ...filtros,
          percentual_referencia: percentualReferencia,
        },
        '/exportador/excel/fluxo-caixa-dw/detalhamento-vendas'
      );
    } catch (err) {
      toast.error(
        getMensagemErro(err, 'Erro ao exportar Detalhamento de Vendas.')
      );
    }
  };

  return (
    <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--full">
      <div className="fluxo-caixa-dw-panel-header">
        <div>
          <h3 className="fluxo-caixa-dw-panel-title">
            <span className="fluxo-caixa-dw-title-marker" />
            Detalhamento de Vendas
          </h3>
          <div className="fluxo-caixa-dw-percentuais mt-2">
            {PERCENTUAIS_REFERENCIA.map(item => (
              <Button
                active={percentualReferencia === item.value}
                key={`percentual-${item.value}`}
                onClick={() => setPercentualReferencia(item.value)}
                size="sm"
                type="button"
                variant={
                  percentualReferencia === item.value
                    ? 'primary'
                    : 'outline-primary'
                }
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>
        <Button
          disabled={linhas.length === 0}
          onClick={exportar}
          size="sm"
          variant="outline-success"
        >
          <FaFileExcel className="mr-1" />
          Excel
        </Button>
      </div>
      <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-panel-body--table">
        {loading ? (
          <Loading label="Carregando Detalhamento de Vendas..." />
        ) : (
          <>
            <TabelaFluxoCaixa
              cellLineHeight="32px"
              cellPadding="0 8px"
              cellWhiteSpace="nowrap"
              colunas={colunas}
              dados={linhas}
              fontSize="12px"
              headerBgColor="#0b4f9c"
              headerLineHeight="34px"
              headerPadding="0 8px"
              minWidth="1052px"
              tableLayout="fixed"
            />
            {renderPaginacao()}
          </>
        )}
      </div>
    </div>
  );
};

export default AbaDetalhamentoVendas;
